import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { monitoringService } from '@shared/monitoring/monitoringService';

/**
 * Global Query Client configured for high performance, stability,
 * and scalable future enhancements (such as offline persistence,
 * infinite scrolling, and real-time syncing).
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const status = (error as any)?.status || (error as any)?.code;
      // Exclude normal expected errors like auth issues or not found keys
      const shouldSuppress = status === 401 || status === 403 || status === 'PGRST116';
      if (!shouldSuppress) {
        monitoringService.captureException('DB', error, 'error', {
          source: 'QueryCache',
          queryKey: query.queryKey,
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      monitoringService.captureException('DB', error, 'error', {
        source: 'MutationCache',
        mutationKey: mutation.options.mutationKey,
      });
    },
  }),
  defaultOptions: {
    queries: {
      // Data is considered fresh for 1 minute by default to prevent duplicate fetches
      staleTime: 1000 * 60 * 1,
      // Unused queries will remain in garbage collection for 10 minutes
      gcTime: 1000 * 60 * 10,
      
      // Retry strategy: exponential backoff for a robust production experience
      retry: (failureCount, error: any) => {
        // Avoid retrying on authentication errors (401/403)
        if (error?.status === 401 || error?.status === 403 || error?.code === 'PGRST116') {
          return false;
        }
        return failureCount < 3; // Retry up to 3 times
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Focus and network reconnection auto-refresh are disabled globally to ensure stability
      // and prevent multiple parallel tab refreshes in sandboxed frames.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Prevents aggressive refresh on standard layout navigation/mounts
    },
    mutations: {
      // Global error handling for mutations could be wired here, or managed per hook
      retry: false,
    },
  },
});

export default queryClient;
